import React, { useState } from 'react';
import UnifiedCalendarItem from '../calendar/UnifiedCalendarItem';

export interface UnifiedCalendarEntry {
  id: number;
  type: 'time_off' | 'task' | 'project';
  title: string;
  dueDate: string;
  status: string;
  request_type?: string;
  start_date?: string;
  end_date?: string;
}

interface UnifiedCalendarViewProps {
  isDark: boolean;
  cardClass: string;
  arrowLeftIcon: React.ReactNode;
  chevronRightIcon: React.ReactNode;
  // Optional props for controlled mode
  entries?: UnifiedCalendarEntry[];
  year?: number;
  month?: number;
  onMonthChange?: (year: number, month: number) => void;
  onItemClick?: (entry: UnifiedCalendarEntry) => void;
}

const MAX_ITEMS_PER_DAY = 3;

const UnifiedCalendarView: React.FC<UnifiedCalendarViewProps> = ({
  isDark,
  cardClass,
  arrowLeftIcon,
  chevronRightIcon,
  entries,
  year,
  month,
  onMonthChange,
  onItemClick,
}) => {
  const now = new Date();
  const [internalYear, setInternalYear] = useState(year ?? now.getFullYear());
  const [internalMonth, setInternalMonth] = useState(
    month ?? now.getMonth() + 1,
  );
  const [expandedDay, setExpandedDay] = useState<number | null>(null);

  // Use controlled values when provided
  const calendarYear = year ?? internalYear;
  const calendarMonth = month ?? internalMonth;
  const calendarData: UnifiedCalendarEntry[] = entries || [];

  const prevMonth = () => {
    const newYear = calendarMonth === 1 ? calendarYear - 1 : calendarYear;
    const newMonth = calendarMonth === 1 ? 12 : calendarMonth - 1;
    if (onMonthChange) {
      onMonthChange(newYear, newMonth);
    } else {
      setInternalYear(newYear);
      setInternalMonth(newMonth);
    }
    setExpandedDay(null);
  };

  const nextMonth = () => {
    const newYear = calendarMonth === 12 ? calendarYear + 1 : calendarYear;
    const newMonth = calendarMonth === 12 ? 1 : calendarMonth + 1;
    if (onMonthChange) {
      onMonthChange(newYear, newMonth);
    } else {
      setInternalYear(newYear);
      setInternalMonth(newMonth);
    }
    setExpandedDay(null);
  };

  const getCalendarDays = () => {
    const firstDay = new Date(calendarYear, calendarMonth - 1, 1).getDay();
    const daysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const getEntriesOnDay = (day: number): UnifiedCalendarEntry[] => {
    const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData.filter((entry) => {
      // Time-off entries span start_date..end_date
      if (entry.type === 'time_off' && entry.start_date && entry.end_date) {
        return entry.start_date <= dateStr && entry.end_date >= dateStr;
      }
      // Tasks/projects: match on dueDate (YYYY-MM-DD)
      if (entry.dueDate) {
        // Normalize to YYYY-MM-DD prefix
        return entry.dueDate.slice(0, 10) === dateStr;
      }
      return false;
    });
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div className={`${cardClass} rounded-2xl p-4`}>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
        >
          {arrowLeftIcon}
        </button>
        <h2
          className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {monthNames[calendarMonth - 1]} {calendarYear}
        </h2>
        <button
          onClick={nextMonth}
          className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
        >
          {chevronRightIcon}
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div
            key={d}
            className={`text-center text-xs font-semibold py-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: getCalendarDays().firstDay }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className={`rounded-lg min-h-[80px] ${isDark ? 'bg-slate-800/30' : 'bg-slate-50'}`}
          />
        ))}

        {Array.from({ length: getCalendarDays().daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dayEntries = getEntriesOnDay(day);
          const isExpanded = expandedDay === day;
          const visibleEntries = isExpanded
            ? dayEntries
            : dayEntries.slice(0, MAX_ITEMS_PER_DAY);
          const hiddenCount = dayEntries.length - MAX_ITEMS_PER_DAY;
          const isToday =
            calendarYear === now.getFullYear() &&
            calendarMonth === now.getMonth() + 1 &&
            day === now.getDate();

          return (
            <div
              key={day}
              className={`border rounded-lg p-1.5 min-h-[80px] ${
                isToday
                  ? isDark
                    ? 'border-violet-500/50 bg-violet-500/10'
                    : 'border-violet-300 bg-violet-50'
                  : isDark
                    ? 'border-slate-700/50 bg-slate-800/30'
                    : 'border-slate-200 bg-white'
              }`}
            >
              <div
                className={`text-right text-xs font-bold mb-1 ${
                  isToday
                    ? isDark
                      ? 'text-violet-400'
                      : 'text-violet-600'
                    : isDark
                      ? 'text-slate-400'
                      : 'text-slate-500'
                }`}
              >
                {day}
              </div>
              <div className="flex flex-col gap-0.5">
                {visibleEntries.map((entry) => (
                  <UnifiedCalendarItem
                    key={`${entry.type}-${entry.id}`}
                    entry={{
                      id: entry.id,
                      type: entry.type,
                      title: entry.title,
                      dueDate: entry.dueDate,
                      status: entry.status,
                      request_type: entry.request_type,
                    }}
                    isDark={isDark}
                    onClick={onItemClick ? () => onItemClick(entry) : undefined}
                  />
                ))}
                {!isExpanded && hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandedDay(day)}
                    className={`text-xs font-semibold rounded px-1.5 py-0.5 text-left transition-colors ${
                      isDark
                        ? 'text-violet-400 hover:bg-slate-700/60'
                        : 'text-violet-600 hover:bg-slate-100'
                    }`}
                  >
                    +{hiddenCount} more
                  </button>
                )}
                {isExpanded && dayEntries.length > MAX_ITEMS_PER_DAY && (
                  <button
                    type="button"
                    onClick={() => setExpandedDay(null)}
                    className={`text-xs font-semibold rounded px-1.5 py-0.5 text-left transition-colors ${
                      isDark
                        ? 'text-violet-400 hover:bg-slate-700/60'
                        : 'text-violet-600 hover:bg-slate-100'
                    }`}
                  >
                    Show less
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UnifiedCalendarView;
